// Dev-only auth bypass. Constructs a synthetic JWT and seeds the token
// storage at app-boot so the existing checkAuth → setUser flow lights up
// the protected routes without an SSO round-trip. Gated behind
// `isDevAuthBypassEnabled()` (env flag AND `import.meta.env.DEV`) so this
// code is dead in any production build.
//
// **Never enable this against a non-local backend.** The token has a
// readable `iss: "dev-bypass"` claim so it's traceable in BE logs, but the
// FE doesn't (and shouldn't) enforce that gate — the BE's Development
// JwtBearer config is what makes the token usable.

import { setAccessToken } from '../utils/authUtils';
import { isDevAuthBypassEnabled } from '../config/authDomain';

const DEV_BYPASS_USER = {
  sub: 'dev-bypass',
  email: 'dev@local.creative-toolbox',
  name: 'Local Dev',
  iss: 'dev-bypass',
};

// Year 2286, sufficiently distant that `isTokenExpired` (authUtils.ts)
// returns false through the foreseeable future. Stored as seconds-since-epoch
// to match the standard JWT `exp` claim shape.
const FAR_FUTURE_EXP_SECONDS = 9_999_999_999;

/**
 * Base64url-encode a UTF-8 string for JWT segment use. `btoa` is ASCII-only,
 * so the `unescape(encodeURIComponent(...))` step is the canonical UTF-8
 * safe shim. We strip standard base64 padding and swap the URL-unsafe
 * `+` / `/` chars per RFC 7515.
 */
function base64Url(input: string): string {
  const utf8 = btoa(unescape(encodeURIComponent(input)));
  return utf8.replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
}

/**
 * Build the synthetic JWT. Exported for testing — production callers should
 * go through `applyDevAuthBypass()` instead.
 */
export function buildDevBypassToken(): string {
  const header = base64Url(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
  const payload = base64Url(
    JSON.stringify({ ...DEV_BYPASS_USER, exp: FAR_FUTURE_EXP_SECONDS }),
  );
  // BE skips signature validation in Development (Program.cs line ~147), so
  // the signature segment can be any non-empty base64url string. Use a
  // human-readable marker so it's obvious in logs / breakpoints.
  const signature = base64Url('dev-bypass-signature');
  return `${header}.${payload}.${signature}`;
}

/**
 * If the bypass is enabled, write the synthetic token into both the cookie
 * and the localStorage backup so `authStore.checkAuth()` picks it up on
 * first mount and seeds `isAuthenticated: true`. No-ops in production
 * builds.
 *
 * Idempotent — safe to call multiple times. Always overwrites the existing
 * token so a stale value from a previous session doesn't shadow the
 * current bypass payload.
 */
export function applyDevAuthBypass(): void {
  if (!isDevAuthBypassEnabled()) return;
  const token = buildDevBypassToken();
  setAccessToken(token);
   
  console.warn(
    '[dev-auth-bypass] Authenticated as Local Dev via synthetic JWT. ' +
      'Disable by clearing VITE_DEV_AUTH_BYPASS in .env or running a production build.',
  );
}
